<template>
    <div
        class="balancer-kpi"
        :class="[
            `balancer-kpi--${kpi.tone || 'neutral'}`,
            kpi.muted ? 'balancer-kpi--muted' : '',
        ]"
    >
        <div class="balancer-kpi__label">
            {{ $t(kpi.label) }}
            <i
                v-if="kpi.hint"
                v-c-tooltip="$t(kpi.hint)"
                class="fas fa-question-circle balancer-kpi__hint"
            ></i>
        </div>
        <div class="balancer-kpi__value">{{ kpi.value }}</div>
        <div class="balancer-kpi__percent">{{ kpi.percentLabel }}</div>
        <!--
            La barra se pinta siempre, aunque no haya porcentaje: si apareciera
            y desapareciera segun la tarjeta, las cajas cambiarian de alto y la
            fila quedaria descuadrada.
        -->
        <div
            class="balancer-kpi__bar"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-valuenow="kpi.percentRounded || 0"
            :aria-label="$t(kpi.label)"
        >
            <div
                class="balancer-kpi__bar-fill"
                :style="{ width: kpi.barWidth || '0%' }"
            ></div>
        </div>
    </div>
</template>

<script>
export default {
    name: "BalancerKpi",
    props: {
        /**
         * Tarjeta a pintar. Forma esperada:
         *
         *   label           clave i18n de la etiqueta
         *   value           numero ya formateado
         *   tone            success | warning | danger | info | neutral
         *   muted           atenua el valor (contadores fuera de la suma)
         *   hint            clave i18n del tooltip (opcional)
         *   percentLabel    "40.0%" o "—"
         *   percentRounded  entero para el aria-valuenow
         *   barWidth        "40%"
         */
        kpi: {
            type: Object,
            required: true,
        },
    },
};
</script>

<style scoped lang="scss">
// Paleta semantica del tema de CoreUI 3, explicita para no depender de
// variables CSS que el tema puede no exponer.
$balancer-tones: (
    "success": #2eb85c,
    "warning": #f9b115,
    "danger": #e55353,
    "info": #3399ff,
    "neutral": #768192,
);

.balancer-kpi {
    position: relative;
    height: 100%;
    padding: 0.75rem 0.875rem;
    border: 1px solid #d8dbe0;
    border-left-width: 3px;
    border-radius: 0.25rem;
    background-color: #fff;

    &__label {
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #768192;
        // Las etiquetas largas no deben romper la altura de la tarjeta ni
        // desalinear los numeros entre columnas.
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__value {
        margin-top: 0.125rem;
        font-size: 1.75rem;
        font-weight: 700;
        line-height: 1.1;
        // Cifras de ancho fijo: los numeros quedan alineados entre tarjetas.
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
    }

    &__percent {
        font-size: 0.8rem;
        font-weight: 600;
        line-height: 1.2;
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
    }

    &__hint {
        margin-left: 0.2rem;
        opacity: 0.55;
        cursor: help;
    }

    &__bar {
        margin-top: 0.5rem;
        height: 4px;
        border-radius: 2px;
        background-color: rgba(118, 129, 146, 0.18);
        overflow: hidden;
    }

    &__bar-fill {
        height: 100%;
        border-radius: inherit;
        // La barra se reanima al cambiar la ventana del filtro de fechas.
        transition: width 420ms cubic-bezier(0.25, 0.8, 0.25, 1);
    }

    @each $name, $color in $balancer-tones {
        &--#{$name} {
            border-left-color: $color;

            .balancer-kpi__percent {
                color: $color;
            }

            .balancer-kpi__bar-fill {
                background-color: $color;
            }
        }
    }

    // Contadores que no forman parte de la misma suma que el resto: se dejan en
    // gris para que no se lean como un cuadre que falla.
    &--muted &__value {
        color: #768192;
        font-weight: 600;
    }
}

// El proyecto tiene tema oscuro (`.c-dark-theme`, `panel/darkMode`): sin esto
// las tarjetas quedarian blancas sobre el card-body oscuro.
.c-dark-theme {
    & .balancer-kpi {
        background-color: rgba(255, 255, 255, 0.04);
        // Solo los tres lados neutros: `border-color` a secas tiene mas
        // especificidad que `.balancer-kpi--<tono>` y se comeria la franja.
        border-top-color: rgba(255, 255, 255, 0.226);
        border-right-color: rgba(255, 255, 255, 0.226);
        border-bottom-color: rgba(255, 255, 255, 0.226);
    }

    & .balancer-kpi__value {
        color: #e1e1e1;
    }

    & .balancer-kpi--muted .balancer-kpi__value {
        color: #9da5b1;
    }

    & .balancer-kpi__bar {
        background-color: rgba(255, 255, 255, 0.12);
    }
}

@media (prefers-reduced-motion: reduce) {
    .balancer-kpi__bar-fill {
        transition: none;
    }
}
</style>
